import { motion } from "framer-motion";
import { Check, ArrowRight, Sparkles } from "lucide-react";

const TIERS = [
  {
    name: "Starter",
    tag: "Entry package",
    price: "AED 1,800",
    inr: "≈ ₹41,000",
    desc: "For boutique brokerages and small teams that need consistent, on-brand social creatives.",
    features: ["8 branded social media posts", "4 edited property reels", "72-hour standard turnaround", "1 revision round per asset", "Brochures available as add-ons"],
    popular: false,
  },
  {
    name: "Growth",
    tag: "Recommended",
    price: "AED 3,500",
    inr: "≈ ₹80,000",
    desc: "For established brokerages with active listings, multiple agents and ongoing marketing needs.",
    features: ["12 branded social media posts", "8 edited property reels", "1 luxury digital brochure / month", "48-hour fast turnaround", "2 revision rounds per asset", "Dubai-hours priority support"],
    popular: true,
  },
  {
    name: "Authority",
    tag: "Premium",
    price: "AED 6,000",
    inr: "≈ ₹1,38,000",
    desc: "For larger brokerages and developers needing higher creative volumes and priority delivery.",
    features: ["16 branded social media posts", "12 edited property reels", "2 digital listing brochures", "2 full listing walkthrough videos", "Priority 24-hour rush turnaround", "Monthly creative strategy call"],
    popular: false,
  },
];

export const Packages = ({ onChoose }) => {
  return (
    <section id="packages" data-testid="packages-section" className="relative py-16 sm:py-24 lg:py-28 bg-[#12203c]">
      <div className="absolute inset-0 stars-bg opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl"
        >
          <p className="font-code text-xs sm:text-sm uppercase tracking-[0.25em] text-[#fba31b] font-bold">Our Packages</p>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Three Packages. <span className="text-[#fba31b]">Choose What Fits Your Goals.</span>
          </h2>
          <p className="mt-4 text-slate-300 text-base sm:text-lg">
            Predictable monthly investment for high-performing property marketing. Every package includes defined deliverables, fast turnaround and a seamless production workflow.
          </p>
        </motion.div>

        <div className="mt-12 grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              data-testid={`package-card-${t.name.toLowerCase()}`}
              className={`card-hover relative flex flex-col rounded-3xl p-7 sm:p-8 bg-[#162544] border ${
                t.popular
                  ? "border-[#fba31b]/70 shadow-[0_0_60px_-15px_rgba(251,163,27,0.4)] md:-translate-y-3"
                  : "border-white/10"
              }`}
            >
              {t.popular && (
                <span data-testid="package-popular-badge" className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-[#fba31b] text-[#12203c] text-xs font-extrabold uppercase tracking-wider px-4 py-1.5 rounded-full">
                  <Sparkles className="w-3.5 h-3.5" /> Most Popular
                </span>
              )}
              <div className="flex items-center justify-between">
                <h3 className="font-display text-xl sm:text-2xl font-bold">{t.name}</h3>
                <span className={`text-[10px] sm:text-xs font-code uppercase tracking-wider px-2.5 py-1 rounded-full ${t.popular ? "bg-[#fba31b]/15 text-[#fba31b]" : "bg-white/5 text-slate-400"}`}>
                  {t.tag}
                </span>
              </div>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="font-display text-3xl sm:text-4xl font-extrabold text-white">{t.price}</span>
                <span className="text-slate-400 text-sm">/month</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">({t.inr})</div>
              <p className="mt-4 text-sm text-slate-300 leading-relaxed">{t.desc}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-200">
                    <Check className="w-4 h-4 mt-0.5 text-[#fba31b] shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`choose-${t.name.toLowerCase()}-btn`}
                onClick={() => onChoose(t.name)}
                className={`mt-8 inline-flex items-center justify-center gap-2 w-full py-3.5 rounded-full font-bold text-sm transition-colors ${
                  t.popular
                    ? "bg-[#fba31b] hover:bg-[#e59312] text-[#12203c]"
                    : "border border-white/20 hover:border-[#fba31b]/60 text-white"
                }`}
              >
                Choose {t.name} <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
